"use client";

import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { ROADMAP_COLORS } from "@/lib/lesson-content/schema";
import MarkdownField from "./MarkdownField";
import { AddBtn, ColorSelect, Field, FieldGroup, RemoveBtn, inputClass } from "./formParts";

/**
 * Form editors for the scenario sort, decision tree and prompt builder, so authors never edit
 * their JSON. Like the other editors they stay loose; the lesson's save checks the schema.
 */

type Set = (patch: Record<string, unknown>) => void;

const smallBtn = "rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30";
const card = "space-y-2 rounded-md border border-slate-200 p-3";

function swap<T>(list: T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir;
  if (j < 0 || j >= list.length) return list;
  const c = [...list];
  [c[i], c[j]] = [c[j], c[i]];
  return c;
}

function MoveBtns({ i, length, onMove }: { i: number; length: number; onMove: (dir: -1 | 1) => void }) {
  return (
    <>
      <button type="button" onClick={() => onMove(-1)} disabled={i === 0} title="Move up" className={smallBtn}>
        <ChevronUp className="h-3.5 w-3.5" />
      </button>
      <button type="button" onClick={() => onMove(1)} disabled={i === length - 1} title="Move down" className={smallBtn}>
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
    </>
  );
}

/** One entry per line. Keeps the raw text while typing, so a blank line in progress isn't lost. */
function LinesInput({ value, onChange, placeholder, rows = 3 }: { value?: string[]; onChange: (next: string[]) => void; placeholder: string; rows?: number }) {
  const [text, setText] = useState(() => (value ?? []).join("\n"));
  return (
    <textarea
      value={text}
      rows={rows}
      onChange={(e) => {
        setText(e.target.value);
        onChange(e.target.value.split("\n").map((l) => l.trim()).filter(Boolean));
      }}
      className={inputClass}
      placeholder={placeholder}
    />
  );
}

/** The shared input style without its full width, for controls that set their own. */
const narrow = inputClass.replace("w-full", "").trim();
const iconInput = `${narrow} w-14 shrink-0 text-center`;

/** A number box that keeps what's typed and only clamps when the author leaves it. */
function MinFilledInput({ value, max, onChange }: { value: number; max: number; onChange: (n: number) => void }) {
  const [text, setText] = useState(String(value));
  return (
    <input
      type="number"
      min={1}
      max={max}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const n = Number(e.target.value);
        if (Number.isInteger(n) && n >= 1 && n <= max) onChange(n);
      }}
      onBlur={() => {
        const n = Math.max(1, Math.min(max, Math.round(Number(text)) || value));
        setText(String(n));
        onChange(n);
      }}
      className={`${narrow} w-24`}
    />
  );
}

// ── Scenario sort ──────────────────────────────────────────────────────────

type SortCategory = { id: string; label: string; color: string; icon?: string; summary?: string };
type SortItem = { id: string; text: string; category_id: string; why?: string };
type Scenario = { id: string; label: string; icon?: string; items: SortItem[]; closing?: string };

export function ScenarioSortEditor({ data, set }: { data: Record<string, unknown>; set: Set }) {
  const d = data as { prompt?: string; categories?: SortCategory[]; scenarios?: Scenario[]; footnote?: string };
  const categories = d.categories ?? [];
  const scenarios = d.scenarios ?? [];
  const setCats = (next: SortCategory[]) => set({ categories: next });
  const setScenarios = (next: Scenario[]) => set({ scenarios: next });
  const setScenario = (i: number, patch: Partial<Scenario>) => setScenarios(scenarios.map((s, si) => (si === i ? { ...s, ...patch } : s)));

  const removeCategory = (i: number) => {
    const remaining = categories.filter((_, ci) => ci !== i);
    const fallback = remaining[0]?.id ?? "";
    const gone = categories[i].id;
    set({
      categories: remaining,
      // Items in the removed category move to the first remaining one, so none is left dangling.
      scenarios: scenarios.map((s) => ({ ...s, items: s.items.map((it) => (it.category_id === gone ? { ...it, category_id: fallback } : it)) })),
    });
  };

  return (
    <>
      <FieldGroup label="Instructions (optional)">
        <MarkdownField value={d.prompt ?? ""} onChange={(v) => set({ prompt: v || undefined })} rows={3} />
      </FieldGroup>

      <FieldGroup label="Categories (2–5): the buttons every item is sorted into">
        <div className="space-y-2">
          {categories.map((c, i) => (
            <div key={c.id} className="flex flex-wrap items-center gap-2">
              <input value={c.icon ?? ""} maxLength={8} onChange={(e) => setCats(categories.map((x, xi) => (xi === i ? { ...x, icon: e.target.value || undefined } : x)))} className={iconInput} placeholder="⚙️" aria-label="Icon" />
              <input value={c.label} onChange={(e) => setCats(categories.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))} className={`${inputClass} min-w-[8rem] flex-1`} placeholder="Name, e.g. Automate" aria-label="Category name" />
              <select value={c.color} onChange={(e) => setCats(categories.map((x, xi) => (xi === i ? { ...x, color: e.target.value } : x)))} className={`${narrow} w-32 shrink-0`} aria-label="Colour">
                {ROADMAP_COLORS.map((k) => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>
              <input value={c.summary ?? ""} onChange={(e) => setCats(categories.map((x, xi) => (xi === i ? { ...x, summary: e.target.value || undefined } : x)))} className={`${inputClass} min-w-[10rem] flex-1`} placeholder="Summary words, e.g. tasks mostly automated" aria-label="Summary words" />
              <RemoveBtn onClick={() => removeCategory(i)} disabled={categories.length <= 2} />
            </div>
          ))}
          {categories.length < 5 ? (
            <AddBtn label="Add category" onClick={() => setCats([...categories, { id: crypto.randomUUID(), label: "", color: ROADMAP_COLORS[categories.length % ROADMAP_COLORS.length] }])} />
          ) : null}
        </div>
      </FieldGroup>

      <FieldGroup label="Scenarios (1–8): learners pick one, sort its items, then compare with the expert">
        <div className="space-y-2">
          {scenarios.map((s, i) => (
            <details key={s.id} className={card} open={scenarios.length === 1}>
              <summary className="cursor-pointer text-sm font-semibold text-slate-800">
                {s.icon ? `${s.icon} ` : ""}
                {s.label || `Scenario ${i + 1}`} <span className="font-normal text-slate-500">· {s.items.length} items</span>
              </summary>
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <input value={s.icon ?? ""} maxLength={8} onChange={(e) => setScenario(i, { icon: e.target.value || undefined })} className={iconInput} placeholder="📣" aria-label="Icon" />
                  <input value={s.label} onChange={(e) => setScenario(i, { label: e.target.value })} className={inputClass} placeholder="Name, e.g. Marketing executive" aria-label="Scenario name" />
                  <MoveBtns i={i} length={scenarios.length} onMove={(dir) => setScenarios(swap(scenarios, i, dir))} />
                  <RemoveBtn onClick={() => setScenarios(scenarios.filter((_, xi) => xi !== i))} disabled={scenarios.length <= 1} />
                </div>
                <span className="block text-xs font-semibold text-slate-600">Items (2–12) with the expert&apos;s category and why</span>
                {s.items.map((it, ii) => {
                  const setItem = (patch: Partial<SortItem>) => setScenario(i, { items: s.items.map((x, xi) => (xi === ii ? { ...x, ...patch } : x)) });
                  return (
                    <div key={it.id} className="space-y-1.5 rounded-md bg-slate-50 p-2">
                      <div className="flex items-center gap-2">
                        <input value={it.text} onChange={(e) => setItem({ text: e.target.value })} className={inputClass} placeholder={`Item ${ii + 1}, e.g. Resize images for different platforms`} aria-label={`Item ${ii + 1}`} />
                        <select value={it.category_id} onChange={(e) => setItem({ category_id: e.target.value })} className={`${narrow} w-44 shrink-0`} aria-label="Expert's category">
                          {categories.map((c) => (
                            <option key={c.id} value={c.id}>{c.label || "Untitled category"}</option>
                          ))}
                        </select>
                        <RemoveBtn onClick={() => setScenario(i, { items: s.items.filter((_, xi) => xi !== ii) })} disabled={s.items.length <= 2} />
                      </div>
                      <input value={it.why ?? ""} onChange={(e) => setItem({ why: e.target.value || undefined })} className={inputClass} placeholder="Why (shown after comparing)" aria-label="Why" />
                    </div>
                  );
                })}
                {s.items.length < 12 ? (
                  <AddBtn label="Add item" onClick={() => setScenario(i, { items: [...s.items, { id: crypto.randomUUID(), text: "", category_id: categories[0]?.id ?? "" }] })} />
                ) : null}
                <input value={s.closing ?? ""} onChange={(e) => setScenario(i, { closing: e.target.value || undefined })} className={inputClass} placeholder="Closing line after comparing (optional)" aria-label="Closing line" />
              </div>
            </details>
          ))}
          {scenarios.length < 8 ? (
            <AddBtn
              label="Add scenario"
              onClick={() =>
                setScenarios([
                  ...scenarios,
                  {
                    id: crypto.randomUUID(),
                    label: "",
                    items: [0, 1].map(() => ({ id: crypto.randomUUID(), text: "", category_id: categories[0]?.id ?? "" })),
                  },
                ])
              }
            />
          ) : null}
        </div>
      </FieldGroup>

      <Field label="Footnote (optional)">
        <input value={d.footnote ?? ""} onChange={(e) => set({ footnote: e.target.value || undefined })} className={inputClass} placeholder="e.g. These are informed judgements, not predictions." />
      </Field>
    </>
  );
}

// ── Decision tree ──────────────────────────────────────────────────────────

type Outcome = { id: string; tone: "good" | "caution" | "stop"; title: string; body: string };
type Question = { id: string; text: string; hint?: string; exit?: { on: "yes" | "no"; outcome_id: string } };

const TONES: { value: Outcome["tone"]; label: string }[] = [
  { value: "good", label: "✅ Good (green)" },
  { value: "caution", label: "🟠 Caution (amber)" },
  { value: "stop", label: "⛔ Stop (red)" },
];

export function DecisionTreeEditor({ data, set }: { data: Record<string, unknown>; set: Set }) {
  const d = data as { prompt?: string; questions?: Question[]; outcomes?: Outcome[]; final_outcome_id?: string };
  const questions = d.questions ?? [];
  const outcomes = d.outcomes ?? [];
  const setQ = (next: Question[]) => set({ questions: next });
  const setQuestion = (i: number, patch: Partial<Question>) => setQ(questions.map((q, qi) => (qi === i ? { ...q, ...patch } : q)));
  const setOutcome = (i: number, patch: Partial<Outcome>) => set({ outcomes: outcomes.map((o, oi) => (oi === i ? { ...o, ...patch } : o)) });

  const removeOutcome = (i: number) => {
    const gone = outcomes[i].id;
    const remaining = outcomes.filter((_, oi) => oi !== i);
    set({
      outcomes: remaining,
      // Questions that ended on the removed outcome no longer end early.
      questions: questions.map((q) => (q.exit?.outcome_id === gone ? { ...q, exit: undefined } : q)),
      final_outcome_id: d.final_outcome_id === gone ? remaining[0]?.id : d.final_outcome_id,
    });
  };
  const outcomeLabel = (o: Outcome, i: number) => o.title || `Outcome ${i + 1}`;

  return (
    <>
      <FieldGroup label="Instructions (optional)">
        <MarkdownField value={d.prompt ?? ""} onChange={(v) => set({ prompt: v || undefined })} rows={3} />
      </FieldGroup>

      <FieldGroup label="Outcomes: the results a learner can land on">
        <div className="space-y-2">
          {outcomes.map((o, i) => (
            <div key={o.id} className={card}>
              <div className="flex items-center gap-2">
                <input value={o.title} onChange={(e) => setOutcome(i, { title: e.target.value })} className={inputClass} placeholder="Title, e.g. A good AI opportunity" aria-label="Outcome title" />
                <select value={o.tone} onChange={(e) => setOutcome(i, { tone: e.target.value as Outcome["tone"] })} className={`${narrow} w-48 shrink-0`} aria-label="Tone">
                  {TONES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <RemoveBtn onClick={() => removeOutcome(i)} disabled={outcomes.length <= 1} />
              </div>
              <textarea value={o.body} onChange={(e) => setOutcome(i, { body: e.target.value })} rows={2} className={inputClass} placeholder="What the learner should do" aria-label="Outcome text" />
            </div>
          ))}
          {outcomes.length < 10 ? (
            <AddBtn label="Add outcome" onClick={() => set({ outcomes: [...outcomes, { id: crypto.randomUUID(), tone: "caution", title: "", body: "" }] })} />
          ) : null}
        </div>
      </FieldGroup>

      <FieldGroup label="Questions (1–10), asked in order with Yes / No buttons">
        <div className="space-y-2">
          {questions.map((q, i) => (
            <div key={q.id} className={card}>
              <div className="flex items-center gap-2">
                <span className="w-5 shrink-0 text-center text-xs font-bold text-slate-400">{i + 1}</span>
                <input value={q.text} onChange={(e) => setQuestion(i, { text: e.target.value })} className={inputClass} placeholder="Question, e.g. Is this task repeated often?" aria-label={`Question ${i + 1}`} />
                <MoveBtns i={i} length={questions.length} onMove={(dir) => setQ(swap(questions, i, dir))} />
                <RemoveBtn onClick={() => setQ(questions.filter((_, xi) => xi !== i))} disabled={questions.length <= 1} />
              </div>
              <input value={q.hint ?? ""} onChange={(e) => setQuestion(i, { hint: e.target.value || undefined })} className={inputClass} placeholder="Hint under the question (optional)" aria-label="Hint" />
              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <span>Ends early if the answer is</span>
                <select
                  value={q.exit?.on ?? ""}
                  onChange={(e) =>
                    setQuestion(i, {
                      exit: e.target.value ? { on: e.target.value as "yes" | "no", outcome_id: q.exit?.outcome_id ?? outcomes[0]?.id ?? "" } : undefined,
                    })
                  }
                  className={`${narrow} w-36`}
                  aria-label="Ends early on"
                >
                  <option value="">never</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
                {q.exit ? (
                  <>
                    <span>and shows</span>
                    <select value={q.exit.outcome_id} onChange={(e) => setQuestion(i, { exit: { on: q.exit!.on, outcome_id: e.target.value } })} className={`${narrow} w-56`} aria-label="Outcome shown">
                      {outcomes.map((o, oi) => (
                        <option key={o.id} value={o.id}>{outcomeLabel(o, oi)}</option>
                      ))}
                    </select>
                  </>
                ) : null}
              </div>
            </div>
          ))}
          {questions.length < 10 ? <AddBtn label="Add question" onClick={() => setQ([...questions, { id: crypto.randomUUID(), text: "" }])} /> : null}
        </div>
      </FieldGroup>

      <Field label="If no question ends it early, show">
        <select value={d.final_outcome_id ?? ""} onChange={(e) => set({ final_outcome_id: e.target.value })} className={inputClass}>
          {outcomes.map((o, oi) => (
            <option key={o.id} value={o.id}>{outcomeLabel(o, oi)}</option>
          ))}
        </select>
      </Field>
    </>
  );
}

// ── Prompt builder ─────────────────────────────────────────────────────────

type BuilderField = { id: string; label: string; question?: string; color?: string; suggestions?: string[]; single?: boolean };
type Brief = { id: string; label: string; icon?: string; suggestions?: Record<string, string[]> };

export function PromptBuilderEditor({ data, set }: { data: Record<string, unknown>; set: Set }) {
  const d = data as {
    prompt?: string;
    briefs?: Brief[];
    fields?: BuilderField[];
    frame_field_id?: string;
    min_filled?: number;
    link?: { label: string; url: string };
  };
  const fields = d.fields ?? [];
  const briefs = d.briefs ?? [];
  const setFields = (next: BuilderField[]) => set({ fields: next });
  const setField = (i: number, patch: Partial<BuilderField>) => setFields(fields.map((f, fi) => (fi === i ? { ...f, ...patch } : f)));
  const setBrief = (i: number, patch: Partial<Brief>) => set({ briefs: briefs.map((b, bi) => (bi === i ? { ...b, ...patch } : b)) });

  const removeField = (i: number) => {
    const gone = fields[i].id;
    set({
      fields: fields.filter((_, fi) => fi !== i),
      // Drop the field from every brief, and stop using it for the frame.
      briefs: briefs.map((b) => {
        const rest = { ...(b.suggestions ?? {}) };
        delete rest[gone];
        return { ...b, suggestions: rest };
      }),
      frame_field_id: d.frame_field_id === gone ? undefined : d.frame_field_id,
      min_filled: Math.min(d.min_filled ?? 3, fields.length - 1),
    });
  };
  const setLink = (patch: Partial<{ label: string; url: string }>) => {
    const next = { label: d.link?.label ?? "", url: d.link?.url ?? "", ...patch };
    set({ link: next.label || next.url ? next : undefined });
  };

  return (
    <>
      <FieldGroup label="Instructions (optional)">
        <MarkdownField value={d.prompt ?? ""} onChange={(v) => set({ prompt: v || undefined })} rows={3} />
      </FieldGroup>

      <FieldGroup label="Fields (2–10): one per part of the prompt, in the order they're joined">
        <div className="space-y-2">
          {fields.map((f, i) => (
            <div key={f.id} className={card}>
              <div className="flex flex-wrap items-center gap-2">
                <input value={f.label} onChange={(e) => setField(i, { label: e.target.value })} className={`${inputClass} min-w-[8rem] flex-1`} placeholder="Name, e.g. Subject" aria-label="Field name" />
                <input value={f.question ?? ""} onChange={(e) => setField(i, { question: e.target.value || undefined })} className={`${inputClass} min-w-[10rem] flex-1`} placeholder="Question, e.g. What's in the picture?" aria-label="Question" />
                <div className="w-36 shrink-0">
                  <ColorSelect value={f.color} autoLabel="Auto colour" onChange={(color) => setField(i, { color })} />
                </div>
                <MoveBtns i={i} length={fields.length} onMove={(dir) => setFields(swap(fields, i, dir))} />
                <RemoveBtn onClick={() => removeField(i)} disabled={fields.length <= 2} />
              </div>
              <LinesInput value={f.suggestions} onChange={(next) => setField(i, { suggestions: next })} placeholder="Suggestions, one per line (used when no brief is picked, or a brief doesn't list its own)" />
              <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={!!f.single} onChange={(e) => setField(i, { single: e.target.checked || undefined })} />
                  A suggestion replaces the text (one choice only)
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={d.frame_field_id === f.id} onChange={(e) => set({ frame_field_id: e.target.checked ? f.id : undefined })} />
                  Sets the frame preview&apos;s shape (e.g. 4:5, 16:9)
                </label>
              </div>
            </div>
          ))}
          {fields.length < 10 ? <AddBtn label="Add field" onClick={() => setFields([...fields, { id: crypto.randomUUID(), label: "", suggestions: [] }])} /> : null}
        </div>
      </FieldGroup>

      <FieldGroup label="Briefs (optional, up to 6): picking one clears the fields and switches to its suggestions">
        <div className="space-y-2">
          {briefs.map((b, i) => (
            <details key={b.id} className={card}>
              <summary className="cursor-pointer text-sm font-semibold text-slate-800">
                {b.icon ? `${b.icon} ` : ""}
                {b.label || `Brief ${i + 1}`}
              </summary>
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <input value={b.icon ?? ""} maxLength={8} onChange={(e) => setBrief(i, { icon: e.target.value || undefined })} className={iconInput} placeholder="🧁" aria-label="Icon" />
                  <input value={b.label} onChange={(e) => setBrief(i, { label: e.target.value })} className={inputClass} placeholder="Name, e.g. Bakery Instagram post" aria-label="Brief name" />
                  <RemoveBtn onClick={() => set({ briefs: briefs.filter((_, xi) => xi !== i) })} />
                </div>
                {fields.map((f) => (
                  <label key={f.id} className="block">
                    <span className="mb-1 block text-xs font-semibold text-slate-600">{f.label || "Untitled field"} suggestions for this brief (empty uses the field&apos;s own)</span>
                    <LinesInput
                      value={b.suggestions?.[f.id]}
                      onChange={(next) => {
                        const rest = { ...(b.suggestions ?? {}) };
                        if (next.length) rest[f.id] = next;
                        else delete rest[f.id];
                        setBrief(i, { suggestions: rest });
                      }}
                      placeholder="One per line"
                    />
                  </label>
                ))}
              </div>
            </details>
          ))}
          {briefs.length < 6 ? <AddBtn label="Add brief" onClick={() => set({ briefs: [...briefs, { id: crypto.randomUUID(), label: "", suggestions: {} }] })} /> : null}
        </div>
      </FieldGroup>

      <Field label="Fields that must have text before the activity counts as done">
        <MinFilledInput key={`${fields.length}-${d.min_filled ?? 3}`} value={d.min_filled ?? 3} max={Math.max(1, fields.length)} onChange={(n) => set({ min_filled: n })} />
      </Field>

      <FieldGroup label="Link button (optional), e.g. to the image tool">
        <div className="flex gap-2">
          <input value={d.link?.label ?? ""} onChange={(e) => setLink({ label: e.target.value })} className={`${narrow} w-48`} placeholder="Open ImagineArt" aria-label="Link text" />
          <input value={d.link?.url ?? ""} onChange={(e) => setLink({ url: e.target.value })} className={inputClass} placeholder="https://www.imagine.art" aria-label="Link URL" />
        </div>
      </FieldGroup>
    </>
  );
}

// ── Spot the errors ────────────────────────────────────────────────────────

type SpotCategory = { id: string; label: string; color: string };
type SpotSentence = { id: string; text: string; category_id?: string; why?: string; fix?: string };
type SpotMissing = { question: string; options: { id: string; label: string; correct?: boolean }[]; category_id?: string; why: string };

export function SpotTheErrorsEditor({ data, set }: { data: Record<string, unknown>; set: Set }) {
  const d = data as {
    prompt?: string;
    source_title?: string;
    source_icon?: string;
    source?: string;
    document_title?: string;
    document_icon?: string;
    categories?: SpotCategory[];
    sentences?: SpotSentence[];
    missing?: SpotMissing;
    allow_notes?: boolean;
    corrected_title?: string;
    corrected?: string;
  };
  const categories = d.categories ?? [];
  const sentences = d.sentences ?? [];
  const setCats = (next: SpotCategory[]) => set({ categories: next });
  const setSentences = (next: SpotSentence[]) => set({ sentences: next });
  const setSentence = (i: number, patch: Partial<SpotSentence>) => setSentences(sentences.map((s, si) => (si === i ? { ...s, ...patch } : s)));
  const removeCategory = (i: number) => {
    const gone = categories[i].id;
    set({
      categories: categories.filter((_, ci) => ci !== i),
      // A sentence labelled with the removed category becomes "fine" rather than pointing at nothing.
      sentences: sentences.map((s) => (s.category_id === gone ? { ...s, category_id: undefined } : s)),
      ...(d.missing?.category_id === gone ? { missing: { ...d.missing, category_id: undefined } } : {}),
    });
  };
  const missing = d.missing;
  const setMissing = (patch: Partial<SpotMissing>) => set({ missing: { ...(missing as SpotMissing), ...patch } });
  const problems = sentences.filter((s) => s.category_id).length + (missing ? 1 : 0);

  return (
    <>
      <FieldGroup label="Instructions (optional)">
        <MarkdownField value={d.prompt ?? ""} onChange={(v) => set({ prompt: v || undefined })} rows={3} />
      </FieldGroup>

      <FieldGroup label="The source of truth (shown on the left)">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input value={d.source_icon ?? ""} maxLength={8} onChange={(e) => set({ source_icon: e.target.value || undefined })} className={iconInput} placeholder="📄" aria-label="Source icon" />
            <input value={d.source_title ?? ""} onChange={(e) => set({ source_title: e.target.value })} className={inputClass} placeholder="Title, e.g. Internal brief (the truth)" aria-label="Source title" />
          </div>
          <MarkdownField value={d.source ?? ""} onChange={(v) => set({ source: v })} rows={6} />
        </div>
      </FieldGroup>

      <FieldGroup label="Labels (2–8): what learners pick for each flagged sentence">
        <div className="space-y-2">
          {categories.map((c, i) => (
            <div key={c.id} className="flex items-center gap-2">
              <input value={c.label} onChange={(e) => setCats(categories.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))} className={inputClass} placeholder="e.g. Accuracy" aria-label="Label" />
              <select value={c.color} onChange={(e) => setCats(categories.map((x, xi) => (xi === i ? { ...x, color: e.target.value } : x)))} className={`${narrow} w-32 shrink-0`} aria-label="Colour">
                {ROADMAP_COLORS.map((k) => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>
              <RemoveBtn onClick={() => removeCategory(i)} disabled={categories.length <= 2} />
            </div>
          ))}
          {categories.length < 8 ? (
            <AddBtn label="Add label" onClick={() => setCats([...categories, { id: crypto.randomUUID(), label: "", color: ROADMAP_COLORS[categories.length % ROADMAP_COLORS.length] }])} />
          ) : null}
        </div>
      </FieldGroup>

      <FieldGroup label={`The text to check, one sentence at a time (3–20). Give a sentence a problem to make it wrong. ${problems} problem${problems === 1 ? "" : "s"} so far.`}>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input value={d.document_icon ?? ""} maxLength={8} onChange={(e) => set({ document_icon: e.target.value || undefined })} className={iconInput} placeholder="✉️" aria-label="Text icon" />
            <input value={d.document_title ?? ""} onChange={(e) => set({ document_title: e.target.value })} className={inputClass} placeholder="Title, e.g. AI-drafted email to principals" aria-label="Text title" />
          </div>
          {sentences.map((s, i) => (
            <div key={s.id} className="space-y-1.5 rounded-md bg-slate-50 p-2">
              <div className="flex items-center gap-2">
                <input value={s.text} onChange={(e) => setSentence(i, { text: e.target.value })} className={inputClass} placeholder={`Sentence ${i + 1}`} aria-label={`Sentence ${i + 1}`} />
                <select
                  value={s.category_id ?? ""}
                  onChange={(e) => setSentence(i, { category_id: e.target.value || undefined })}
                  className={`${narrow} w-40 shrink-0`}
                  aria-label="Problem"
                >
                  <option value="">Fine</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.label || "Untitled label"}</option>
                  ))}
                </select>
                <MoveBtns i={i} length={sentences.length} onMove={(dir) => setSentences(swap(sentences, i, dir))} />
                <RemoveBtn onClick={() => setSentences(sentences.filter((_, xi) => xi !== i))} disabled={sentences.length <= 3} />
              </div>
              {s.category_id ? (
                <>
                  <input value={s.why ?? ""} onChange={(e) => setSentence(i, { why: e.target.value || undefined })} className={inputClass} placeholder="What's wrong with it (shown after checking)" aria-label="Why" />
                  <input value={s.fix ?? ""} onChange={(e) => setSentence(i, { fix: e.target.value || undefined })} className={inputClass} placeholder="Corrected sentence (optional, shown in the answer key)" aria-label="Fix" />
                </>
              ) : null}
            </div>
          ))}
          {sentences.length < 20 ? <AddBtn label="Add sentence" onClick={() => setSentences([...sentences, { id: crypto.randomUUID(), text: "" }])} /> : null}
        </div>
      </FieldGroup>

      <FieldGroup label="Something missing (optional): counts as one more problem">
        {missing ? (
          <div className={card}>
            <input value={missing.question} onChange={(e) => setMissing({ question: e.target.value })} className={inputClass} placeholder="e.g. Is anything important missing from the email? Select any that apply." aria-label="Question" />
            {missing.options.map((o, oi) => (
              <div key={o.id} className="flex items-center gap-2">
                <label className="flex shrink-0 items-center gap-1.5 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    checked={!!o.correct}
                    onChange={(e) => setMissing({ options: missing.options.map((x, xi) => (xi === oi ? { ...x, correct: e.target.checked } : x)) })}
                  />
                  Missing
                </label>
                <input value={o.label} onChange={(e) => setMissing({ options: missing.options.map((x, xi) => (xi === oi ? { ...x, label: e.target.value } : x)) })} className={inputClass} placeholder={`Option ${oi + 1}`} aria-label={`Option ${oi + 1}`} />
                <RemoveBtn onClick={() => setMissing({ options: missing.options.filter((_, xi) => xi !== oi) })} disabled={missing.options.length <= 2} />
              </div>
            ))}
            {missing.options.length < 6 ? <AddBtn label="Add option" onClick={() => setMissing({ options: [...missing.options, { id: crypto.randomUUID(), label: "" }] })} /> : null}
            <div className="flex items-center gap-2">
              <span className="shrink-0 text-xs text-slate-600">Label</span>
              <select value={missing.category_id ?? ""} onChange={(e) => setMissing({ category_id: e.target.value || undefined })} className={`${narrow} w-44`} aria-label="Missing label">
                <option value="">None</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.label || "Untitled label"}</option>
                ))}
              </select>
            </div>
            <input value={missing.why} onChange={(e) => setMissing({ why: e.target.value })} className={inputClass} placeholder="What's missing and why it matters (shown after checking)" aria-label="Why" />
            <button type="button" onClick={() => set({ missing: undefined })} className="text-xs font-semibold text-rose-600 hover:underline">
              Remove the missing question
            </button>
          </div>
        ) : (
          <AddBtn
            label="Add a missing question"
            onClick={() =>
              set({
                missing: {
                  question: "Is anything important missing? Select any that apply.",
                  options: [0, 1].map((n) => ({ id: crypto.randomUUID(), label: "", correct: n === 0 })),
                  why: "",
                },
              })
            }
          />
        )}
      </FieldGroup>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={d.allow_notes !== false} onChange={(e) => set({ allow_notes: e.target.checked })} />
        Let learners type a correction under each flag (never judged)
      </label>

      <FieldGroup label="The corrected version (optional, shown after checking)">
        <div className="space-y-2">
          <input value={d.corrected_title ?? ""} onChange={(e) => set({ corrected_title: e.target.value || undefined })} className={inputClass} placeholder="Title, e.g. The corrected email" aria-label="Corrected title" />
          <MarkdownField value={d.corrected ?? ""} onChange={(v) => set({ corrected: v || undefined })} rows={5} />
        </div>
      </FieldGroup>
    </>
  );
}
